import React, {useState, useCallback} from "react";
import {Tab} from "@headlessui/react";
import {THEMES} from "../data/books";
import {PlusIcon, TrashIcon, XMarkIcon} from "@heroicons/react/24/outline";
import useZustandStore from "../hooks/useZustandStore";
import {useBookmarks} from "../hooks/useBook";
import {createBookmark, deleteBookmark} from "../api/book";
import {useQueryClient} from "@tanstack/react-query";

const FONT_OPTIONS = [
    {label: "宋体", sample: "大学之道在明明德", value: "'Noto Serif SC','Songti SC','STSong',serif"},
    {label: "霞鶩文楷", sample: "大学之道在明明德", value: "'LXGW WenKai','KaiTi','STKaiti',serif"},
    {label: "楷体", sample: "大学之道在明明德", value: "'KaiTi','STKaiti','楷体',serif"},
    {label: "仿宋", sample: "大学之道在明明德", value: "'FangSong','STFangsong','仿宋',serif"},
];

export default function RightPanel({
                                       focusMode,
                                       activeChar,
                                       settings,
                                       onSettingChange,
                                       onTheme,
                                       currentChapter,
                                       onChapterSelect,
                                       chapters,
                                       activeChapterId,
                                       isMobile,
                                       onClose,
                                   }) {
    return (
        <aside
            className="h-full w-panel flex flex-col flex-shrink-0 overflow-hidden border-l border-paper-deep transition-opacity duration-500 bg-gradient-to-b from-[#faf3e4] to-[#f0e5c8]"
            style={{opacity: focusMode ? 0.05 : 1}}
        >
            {/* ── 移动端关闭按钮 ── */}
            {isMobile && (
                <div className="flex items-center justify-between px-4 py-3 border-b border-paper-deep">
                    <span className="text-[11px] tracking-[3px] text-ink-faint font-serif">面板</span>
                    <button onClick={onClose} className="text-ink-faint hover:text-ink p-1" aria-label="关闭面板">
                        <XMarkIcon className="w-5 h-5"/>
                    </button>
                </div>
            )}
            <Tab.Group>
                {/* Tab headers */}
                <Tab.List className="flex flex-shrink-0 border-b border-paper-deep bg-paper-warm">
                    {["注 解", "目 录", "书 签", "设 置"].map((tab) => (
                        <Tab
                            key={tab}
                            className={({selected}) =>
                                `flex-1 py-3.5 text-xs tracking-[2px] font-serif cursor-pointer border-none
                transition-all duration-150 relative -mb-px outline-none
                ${selected
                                    ? "text-vermilion border-b-2 border-vermilion bg-paper"
                                    : "text-ink-faint hover:text-ink-light border-b-2 border-transparent"
                                }`
                            }
                        >
                            {tab}
                        </Tab>
                    ))}
                </Tab.List>

                {/* Tab panels */}
                <Tab.Panels className="flex-1 overflow-y-auto scrollbar-thin-paper">
                    <Tab.Panel className="p-4 outline-none h-full flex flex-col">
                        <AnnotationTab activeChar={activeChar}/>
                    </Tab.Panel>

                    <Tab.Panel className="p-4 outline-none">
                        <TocTab
                            chapters={chapters}
                            activeChapterId={activeChapterId}
                            onSelect={onChapterSelect}
                        />
                    </Tab.Panel>

                    <Tab.Panel className="p-4 outline-none">
                        <BookmarksTab/>
                    </Tab.Panel>

                    <Tab.Panel className="p-5 outline-none">
                        <SettingsTab settings={settings} onChange={onSettingChange} onTheme={onTheme}/>
                    </Tab.Panel>
                </Tab.Panels>
            </Tab.Group>
        </aside>
    );
}

/* ─────────────────────────────────────── Annotation Tab */
function AnnotationTab({activeChar}) {
    const activeAnnotation = useZustandStore(state => state.activeAnnotation);
    const charDataMap = useZustandStore(state => state.charDataMap);
    const data = activeChar ? charDataMap[activeChar] : null;

    return (
        <div className="flex flex-col h-full">
            {/* ── 上部：当前标注 ── */}
            <div className="flex-shrink-0">
                <p className="text-xs tracking-[3px] text-gold mb-3">注 解</p>
                {activeAnnotation ? (
                    <div className="bg-white border border-paper-deep rounded-lg px-4 py-3 shadow-card">
                        <p className="text-[15px] text-vermilion font-serif tracking-wide mb-1.5">{activeAnnotation.text}</p>
                        <p className="text-sm text-ink-light leading-relaxed">{activeAnnotation.note}</p>
                    </div>
                ) : (
                    <p className="text-[13px] text-ink-faint/40 font-serif tracking-[2px]">
                        指向正文中有下划线的文字
                    </p>
                )}
            </div>

            {/* ── 分隔 ── */}
            <div className="flex items-center gap-2 my-4">
                <div className="flex-1 h-px bg-paper-deep"/>
                <span className="text-xs tracking-[3px] text-gold/60">字 典</span>
                <div className="flex-1 h-px bg-paper-deep"/>
            </div>

            {/* ── 下部：字词典 ── */}
            <div className="flex-1 min-h-0">
                {data ? (
                    <div className="bg-white border border-paper-deep rounded-lg px-4 py-3 shadow-card">
                        <div className="flex items-center gap-3 mb-2">
                            <span className="font-display text-[28px] text-ink leading-none">{activeChar}</span>
                            <span className="text-xs text-ink-faint tracking-[2px]">{data?.['Pinyin']}</span>
                        </div>
                        <LookupSection label="字义" text={data?.['Definition']}/>
                        <LookupSection label="引文" text={data?.['Example']} italic/>
                        <LookupSection label="部首" text={data?.['Radical']}/>
                    </div>
                ) : (
                    <p className="text-[13px] text-ink-faint/40 font-serif tracking-[2px] mt-4 text-center">
                        点击正文中的字查看释义
                    </p>
                )}
            </div>
        </div>
    );
}

function LookupSection({label, text, italic}) {
    return (
        <div className="mb-3">
            <div className="flex items-center gap-1.5 mb-1.5">
                <span className="text-[10px] tracking-[3px] text-gold">{label}</span>
                <div className="flex-1 h-px bg-paper-deep"/>
            </div>
            <p
                className={`text-[13px] leading-relaxed text-ink-light
          ${italic ? "italic pl-3 border-l-2 border-paper-deep" : ""}`}
            >
                {text}
            </p>
        </div>
    );
}

/* ─────────────────────────────────────── TOC Tab */
function TocTab({chapters, activeChapterId, onSelect}) {
    if (!chapters || chapters.length === 0) {
        return (
            <p className="text-[12px] text-ink-faint/50 text-center mt-10 font-serif tracking-[3px]">
                请先从侧边栏选择书目
            </p>
        );
    }
    return (
        <>
            {chapters.map((ch) => (
                <TocItem
                    key={ch.id}
                    item={{num: ch.ord + 1, title: ch.title}}
                    isCurrent={activeChapterId === ch.id}
                    onClick={() => onSelect(ch.id)}
                />
            ))}
        </>
    );
}

function TocItem({item, isCurrent, onClick}) {
    return (
        <button
            onClick={onClick}
            className={`
        w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md mb-0.5
        font-serif text-left transition-all duration-150
        border-l-2
        ${isCurrent
                ? "bg-vermilion/8 border-vermilion"
                : "border-transparent hover:bg-paper-deep"
            }
      `}
        >
            <span className="text-[10px] text-ink-faint w-6 tracking-wide">{item.num}</span>
            <span className={`text-[13px] tracking-wide flex-1 ${isCurrent ? "text-vermilion" : "text-ink-light"}`}>
        {item.title}
      </span>
        </button>
    );
}

/* ─────────────────────────────────────── Bookmarks Tab */
function BookmarksTab() {
    const activeBookId = useZustandStore(s => s.activeBookId);
    const activeChapterId = useZustandStore(s => s.activeChapterId);
    const {data: bookmarksData, refetch} = useBookmarks(activeBookId);
    const queryClient = useQueryClient();
    const marks = bookmarksData?.data || [];

    const handleDelete = useCallback(async (id) => {
        try {
            await deleteBookmark(id);
            queryClient.invalidateQueries({queryKey: ['bookmarks', activeBookId]});
        } catch (e) {
            console.error('删除书签失败', e);
        }
    }, [queryClient, activeBookId]);

    const handleAdd = useCallback(async () => {
        const quote = window.getSelection()?.toString().trim();
        if (!quote) {
            alert('请先在正文中选中要标注的文字');
            return;
        }
        try {
            await createBookmark({
                book_id: activeBookId,
                chapter_id: activeChapterId,
                quote,
                note: '',
                position: 0,
            });
            queryClient.invalidateQueries({queryKey: ['bookmarks', activeBookId]});
        } catch (e) {
            console.error('添加书签失败', e);
        }
    }, [queryClient, activeBookId, activeChapterId]);

    return (
        <>
            {marks.length === 0 ? (
                <p className="text-[12px] text-ink-faint/50 text-center mt-10 font-serif tracking-[3px]">
                    暂无书签，选中正文后点击下方按钮添加
                </p>
            ) : (
                marks.map((b) => (
                    <div
                        key={b.id}
                        className="p-3 border border-paper-deep rounded-md mb-2 bg-white group relative"
                    >
                        <p className="text-[13px] leading-relaxed text-ink-light mb-1.5 pr-6">{b.quote}</p>
                        <p className="text-[11px] text-ink-faint tracking-wide">{b.created_at?.slice(0, 10)}</p>
                        <button
                            onClick={() => handleDelete(b.id)}
                            className="absolute top-2 right-2 text-ink-faint/30 hover:text-vermilion transition-colors opacity-0 group-hover:opacity-100"
                        >
                            <TrashIcon className="w-3.5 h-3.5"/>
                        </button>
                    </div>
                ))
            )}
            <div className="text-center mt-4">
                <button
                    onClick={handleAdd}
                    className="inline-flex items-center gap-1.5 border border-dashed border-paper-deep rounded px-5 py-2 text-[12px] tracking-[2px] text-ink-faint font-serif hover:border-gold/50 hover:text-gold transition-colors">
                    <PlusIcon className="w-3.5 h-3.5"/>
                    添加书签
                </button>
            </div>
        </>
    );
}

/* ─────────────────────────────────────── Settings Tab */
function SettingsTab({settings, onChange, onTheme}) {
    return (
        <>
            <p className="text-[11px] tracking-[3px] text-ink-faint mb-4">阅 读 设 置</p>

            {/* Toggles */}
            {[
                {key: "showTranslation", label: "显示今译"},
                {key: "showCommentary", label: "显示注疏"},
                {key: "showPinyin", label: "字符注音"},
                {key: "focusMode", label: "专注模式"},
            ].map(({key, label}) => (
                <div key={key} className="flex items-center justify-between py-3 border-b border-paper-deep">
                    <span className="text-[13px] text-ink-light tracking-wide">{label}</span>
                    <Toggle on={settings[key]} onClick={() => onChange(key, !settings[key])}/>
                </div>
            ))}

            {/* Theme */}
            <div className="mt-5">
                <p className="text-[11px] tracking-[3px] text-ink-faint mb-3">纸 张 色 调</p>
                <div className="flex gap-2">
                    {THEMES.map((t) => (
                        <button
                            key={t.label}
                            onClick={() => onTheme(t)}
                            title={t.label}
                            className={`
                w-6 h-6 rounded-full transition-all duration-150
                ${settings.themeLabel === t.label
                                ? "ring-2 ring-offset-1 ring-ink/50 scale-110"
                                : "ring-1 ring-black/15 hover:scale-110"
                            }
              `}
                            style={{background: t.paper}}
                        />
                    ))}
                </div>
            </div>

            {/* Line height */}
            <div className="mt-5">
                <p className="text-[11px] tracking-[3px] text-ink-faint mb-3">行 间 距</p>
                <input
                    type="range" min="1.8" max="3.0" step="0.1"
                    value={settings.lineHeight}
                    onChange={(e) => onChange("lineHeight", parseFloat(e.target.value))}
                    className="w-full accent-vermilion"
                />
            </div>

            {/* Font */}
            <div className="mt-5">
                <p className="text-[11px] tracking-[3px] text-ink-faint mb-3">字 体 选 择</p>
                <div className="flex flex-col gap-2">
                    {FONT_OPTIONS.map((f) => {
                        const active = settings.fontFamily === f.value
                        return (
                            <button
                                key={f.label}
                                onClick={() => onChange("fontFamily", f.value)}
                                className={`
                                    text-left rounded-lg border transition-all duration-150 px-3 py-2
                                    ${active
                                        ? "border-vermilion bg-vermilion/5 shadow-sm"
                                        : "border-paper-deep hover:border-ink-faint"
                                    }
                                `}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-[12px] text-ink-faint tracking-wide">{f.label}</span>
                                    {active && <span className="text-vermilion text-[11px]">●</span>}
                                </div>
                                <div
                                    className="text-[16px] text-ink mt-1 leading-relaxed"
                                    style={{ fontFamily: f.value }}
                                >
                                    {f.sample}
                                </div>
                            </button>
                        )
                    })}
                </div>
            </div>
        </>
    );
}

/* Toggle switch component */
function Toggle({on, onClick}) {
    return (
        <button
            onClick={onClick}
            className={`
        relative w-10 h-[22px] rounded-full transition-colors duration-200 flex-shrink-0
        ${on ? "bg-jade" : "bg-paper-deep"}
      `}
        >
      <span
          className={`
          absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm
          transition-all duration-200
          ${on ? "left-[21px]" : "left-[3px]"}
        `}
      />
        </button>
    );
}
